import Decimal from 'decimal.js';
import { create } from 'zustand';

import { centsFromDollarsString } from 'models/dollars';
import type { LifecycleDTO, TickerDTO, WsMessage, WsResponse } from 'models/ws';
import { isSlowNetworkConnection } from 'utils/device';

import { createTickerWatcher } from './createTickerWatcher';

export interface QuoteData {
    /** Original dollar precision for financial calculations. */
    priceDollars?: string;
    yesBid: number;
    yesAsk: number;
    noBid: number;
    noAsk: number;
    price: number;
    /**
     * Contracts traded / open, halved from the `_fp` fixed-point fields exactly as the entity
     * layer does — a two-sided fill counts once per side on the wire. Carried here (rather than
     * left to REST) because consumers that overlay a live quote onto a `MarketEntity` need the
     * same nine fields the `ticker` channel has always supplied; without these, event volume
     * and open-interest displays have no live source at all.
     */
    volume: number;
    openInterest: number;
}

export interface LifecycleStatus {
    isDeactivated: boolean | null;
    result: string | null;
    closedTs: number | null;
    determinationTs: number | null;
    settledTs: number | null;
    // Cents (FixedPointDollars × 100). The wire field is `settlement_value` —
    // a string of dollars (e.g. "12.3400") set only on `market_lifecycle_determined`.
    settlementValueCents: number | null;
}

interface TickerQuotesActions {
    watch: (ticker: string) => void;
    unwatch: (ticker: string) => void;
}

interface TickerQuotesState {
    quotes: Record<string, QuoteData>;
    statuses: Record<string, LifecycleStatus>;
    quotesVersion: number;
    statusesVersion: number;
    actions: TickerQuotesActions;
}

type SetFn = (partial: Partial<TickerQuotesState> | ((state: TickerQuotesState) => Partial<TickerQuotesState>)) => void;
type GetFn = () => TickerQuotesState;

let storeSet: SetFn;
let storeGet: GetFn;

function parseTickerQuote(dto: TickerDTO): QuoteData {
    // Prefer the `_dollars` strings so sub-cent markets (deci_cent /
    // tapered_deci_cent) keep tenth-of-cent precision; the integer fields are
    // legacy whole-cent rounds and would collapse 99.5¢ to 99 or 100.
    const yesBid = centsFromDollarsString(dto.yes_bid_dollars, dto.yes_bid);
    const yesAsk = centsFromDollarsString(dto.yes_ask_dollars, dto.yes_ask);
    return {
        yesBid,
        yesAsk,
        noBid: new Decimal(100).minus(yesAsk).toNumber(),
        noAsk: new Decimal(100).minus(yesBid).toNumber(),
        price: centsFromDollarsString(dto.price_dollars, dto.price),
        priceDollars: dto.price_dollars ?? new Decimal(dto.price).div(100).toString(),
        volume: dto.volume_fp ? parseFloat(dto.volume_fp) / 2 : dto.dollar_volume,
        openInterest: dto.open_interest_fp ? parseFloat(dto.open_interest_fp) / 2 : dto.dollar_open_interest,
    };
}

// The transport dispatches each message in a WS frame separately (see the per-message loop in
// `ws.ts`), so a many-market event fans a single frame into dozens of `handleTickerMessages`
// calls. Applying each as its own synchronous `set` re-renders every `useSyncExternalStore`
// consumer once per message — on a high-market event that nests past React's update-depth ceiling
// ("Maximum update depth exceeded"). Buffer the raw messages and apply them in a single `set` on
// a scheduled task, collapsing a per-frame burst into one store update / one render.
let pendingTickerMessages: WsMessage[] = [];
let tickerFlushScheduled = false;
const SLOW_NETWORK_TICKER_FLUSH_TIMEOUT_MS = 2000;

function flushTickerMessages(): void {
    tickerFlushScheduled = false;
    const messages = pendingTickerMessages;
    pendingTickerMessages = [];

    const current = storeGet().quotes;

    // Coalesce by ticker first (last message wins) so multiple ticker messages for the
    // same market in one batch collapse to the newest packet before we diff. Diffing each
    // message against the pre-batch `current` instead would let an earlier differing
    // message win the write while the newest — which may match `current` — is dropped as a
    // no-op, leaving the quote stale (e.g. batch [B, A] when the store already holds A).
    const latestByTicker = new Map<string, QuoteData>();
    for (const msg of messages) {
        if (msg.type === 'ticker') {
            const dto = msg.msg as TickerDTO;
            // A ticker can be unwatched between when its packet was buffered and this flush;
            // `onTickersDropped` already deleted the cached quote, so skip the buffered packet
            // rather than resurrecting a stale entry for a ticker with refCount 0.
            if (!watcher.isWatched(dto.market_ticker)) continue;
            latestByTicker.set(dto.market_ticker, parseTickerQuote(dto));
        }
    }

    const updates: Array<[string, QuoteData]> = [];
    for (const [ticker, quote] of latestByTicker) {
        const existing = current[ticker];
        if (
            !existing ||
            existing.yesBid !== quote.yesBid ||
            existing.yesAsk !== quote.yesAsk ||
            existing.price !== quote.price ||
            existing.volume !== quote.volume ||
            existing.openInterest !== quote.openInterest
        ) {
            updates.push([ticker, quote]);
        }
    }

    if (updates.length === 0) return;

    storeSet((state) => {
        for (const [ticker, quote] of updates) state.quotes[ticker] = quote;
        return { quotes: state.quotes, quotesVersion: state.quotesVersion + 1 };
    });
}

function handleTickerMessages(response: WsResponse): void {
    const messages: WsMessage[] = Array.isArray(response) ? response : [response];
    for (const msg of messages) pendingTickerMessages.push(msg);
    if (!tickerFlushScheduled) {
        tickerFlushScheduled = true;
        // This store is read via `useSyncExternalStore` (synchronous re-render per `set`). On
        // 3G or Save-Data connections, wait for idle so a high-frequency many-market burst does
        // not compete with hydration and LCP; the timeout keeps quotes boundedly fresh on a busy
        // page. A macrotask remains important on normal connections: it yields between flushes
        // and resets React's nested-update counter while coalescing the burst into one `set`.
        if (isSlowNetworkConnection()) {
            if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(flushTickerMessages, { timeout: SLOW_NETWORK_TICKER_FLUSH_TIMEOUT_MS });
            } else if (typeof window !== 'undefined') {
                window.setTimeout(flushTickerMessages, SLOW_NETWORK_TICKER_FLUSH_TIMEOUT_MS);
            } else {
                setTimeout(flushTickerMessages, SLOW_NETWORK_TICKER_FLUSH_TIMEOUT_MS);
            }
            return;
        }
        setTimeout(flushTickerMessages, 0);
    }
}

function lifecycleStatusEquals(a: LifecycleStatus | undefined, b: LifecycleStatus): boolean {
    return (
        !!a &&
        a.isDeactivated === b.isDeactivated &&
        a.result === b.result &&
        a.closedTs === b.closedTs &&
        a.determinationTs === b.determinationTs &&
        a.settledTs === b.settledTs &&
        a.settlementValueCents === b.settlementValueCents
    );
}

let pendingLifecycleMessages: WsMessage[] = [];
let lifecycleFlushScheduled = false;

function flushLifecycleMessages(): void {
    lifecycleFlushScheduled = false;
    const messages = pendingLifecycleMessages;
    pendingLifecycleMessages = [];

    const updates: Record<string, Partial<LifecycleStatus>> = {};

    for (const msg of messages) {
        if (msg.type === 'market_lifecycle_v2') {
            const dto = msg.msg as LifecycleDTO;
            // Lifecycle is a global broadcast — drop events for tickers no caller
            // is watching so `statuses` doesn't accumulate the entire market universe.
            if (!watcher.isWatched(dto.market_ticker)) continue;
            const update: Partial<LifecycleStatus> = {};
            if (dto.is_deactivated !== undefined) update.isDeactivated = dto.is_deactivated;
            if (dto.result !== undefined) update.result = dto.result;
            if (dto.close_ts !== undefined) update.closedTs = dto.close_ts;
            if (dto.determination_ts !== undefined) update.determinationTs = dto.determination_ts;
            if (dto.settled_ts !== undefined) update.settledTs = dto.settled_ts;
            if (dto.settlement_value != null) {
                update.settlementValueCents = new Decimal(dto.settlement_value).mul(100).toNumber();
            }
            if (Object.keys(update).length > 0) {
                updates[dto.market_ticker] = { ...updates[dto.market_ticker], ...update };
            }
        }
    }

    if (Object.keys(updates).length === 0) return;

    const changedStatuses: Array<[string, LifecycleStatus]> = [];
    const current = storeGet().statuses;
    for (const [ticker, update] of Object.entries(updates)) {
        const existing = current[ticker];
        const nextStatus = {
            isDeactivated: update.isDeactivated ?? existing?.isDeactivated ?? null,
            result: update.result ?? existing?.result ?? null,
            closedTs: update.closedTs ?? existing?.closedTs ?? null,
            determinationTs: update.determinationTs ?? existing?.determinationTs ?? null,
            settledTs: update.settledTs ?? existing?.settledTs ?? null,
            settlementValueCents: update.settlementValueCents ?? existing?.settlementValueCents ?? null,
        };
        if (!lifecycleStatusEquals(existing, nextStatus)) changedStatuses.push([ticker, nextStatus]);
    }

    if (changedStatuses.length === 0) return;

    storeSet((state) => {
        for (const [ticker, status] of changedStatuses) state.statuses[ticker] = status;
        return { statuses: state.statuses, statusesVersion: state.statusesVersion + 1 };
    });
}

function handleLifecycleMessages(response: WsResponse): void {
    const messages: WsMessage[] = Array.isArray(response) ? response : [response];
    for (const msg of messages) pendingLifecycleMessages.push(msg);
    if (!lifecycleFlushScheduled) {
        lifecycleFlushScheduled = true;
        // Macrotask, not microtask — see the note in `handleTickerMessages`.
        setTimeout(flushLifecycleMessages, 0);
    }
}

const watcher = createTickerWatcher({
    channels: [
        { channel: 'ticker', handler: handleTickerMessages, sendInitialSnapshot: true },
        // Gateway treats `market_lifecycle_v2` as a global broadcast (`market_tickers`
        // ignored, `update_subscription` no-op'd), so subscribe once with no filter
        // and let the reducer drop events for tickers no caller is watching.
        { channel: 'market_lifecycle_v2', handler: handleLifecycleMessages, globalBroadcast: true },
    ],
    // A transport outage freezes the cached quotes at their last values — while consumers keep
    // preferring them over fresher REST entities (`applyLiveMarketDataToEntity`) and
    // `useHasLiveMarketQuote` keeps the fast REST-polling fallbacks disabled. Drop the quotes so
    // the overlay falls back to REST data and the fallbacks re-arm for the duration of the outage;
    // the reconnect re-subscribes and re-delivers a snapshot per ticker. `statuses` are kept:
    // lifecycle facts (determination/settlement) don't go stale while the socket is down.
    onDisconnect: () => {
        // Also drop packets buffered but not yet flushed — applying them after the clear would
        // resurrect pre-outage quotes and re-disable the fallbacks with stale data.
        pendingTickerMessages = [];
        const { quotes } = storeGet();
        if (Object.keys(quotes).length === 0) return;
        storeSet((state) => ({ quotes: {}, quotesVersion: state.quotesVersion + 1 }));
    },
    // Forget cached quotes/statuses for tickers nobody is watching anymore so the
    // store doesn't accumulate entries forever as users navigate between cards.
    onTickersDropped: (tickers) => {
        const { quotes, statuses } = storeGet();
        const shouldDropQuotes = tickers.some((ticker) => ticker in quotes);
        const shouldDropStatuses = tickers.some((ticker) => ticker in statuses);
        if (!shouldDropQuotes && !shouldDropStatuses) return;

        storeSet((state) => {
            for (const ticker of tickers) {
                delete state.quotes[ticker];
                delete state.statuses[ticker];
            }
            return {
                quotes: state.quotes,
                statuses: state.statuses,
                ...(shouldDropQuotes && { quotesVersion: state.quotesVersion + 1 }),
                ...(shouldDropStatuses && { statusesVersion: state.statusesVersion + 1 }),
            };
        });
    },
});

export const useTickerQuotesStore = create<TickerQuotesState>()((set, get) => {
    storeSet = set;
    storeGet = get;

    return {
        quotes: {},
        statuses: {},
        quotesVersion: 0,
        statusesVersion: 0,
        actions: {
            watch: watcher.watch,
            unwatch: watcher.unwatch,
        },
    };
});
